/**
 * HOME-01 — the one listing shape the redesign's cards render.
 *
 * Three render paths feed the same card from three different sources: the Vike
 * SSR page hands over `HomepageListingCard` rows built by
 * src/pages/index/+data.tsx, the SPA hands over `Property` objects built by
 * `dbListingToProperty`, and — since fix round 1 — the SPA also hands over
 * `SyndicatedListing` rows straight off `syndicated_public`. Rather than
 * teaching the card about all three, each maps into this view-model once.
 *
 * The mappers take STRUCTURAL inputs, not the named `Property` type. There are
 * two `Property` interfaces in this repo — types.ts (no `title`) and
 * lib/api/types.ts (with it) — and a mapper that named either one would force
 * every caller through the wrong one. Structural inputs let both satisfy it
 * without a cast and without adding to the tsc baseline.
 *
 * WHAT `source` IS FOR (fix round 1)
 * A marketplace listing and an indexed announcement are not the same object
 * with fields missing, and the card must not treat them as one. `source` is
 * carried all the way to the render so that the differences stay visible in
 * code: an announcement has no baths, no floor area, no save route and no
 * agent — it has a company that published it, a bed range, and the date WE
 * indexed it. Every one of those is a rule about what we may claim, so the
 * card reads `source` and renders the row it actually has rather than a
 * marketplace row with holes in it.
 *
 * Nothing here derives a number that the database does not hold. Fields the
 * current queries do not fetch (per-listing agent verification) arrive as
 * their empty value, and the modules render the absence rather than a guess.
 *
 * SALE ONLY. No rental field exists in either table, and none may be added
 * here.
 */

import { formatPropertyType } from '../../../utils/formatting';
import { formatListingPrice } from '../../../utils/formatCurrency';
import { savedTargetKey, type SavedTarget } from '../../../lib/api/savedTargets';
import {
  formatSyndicatedLocation,
  syndicatedPriceLine,
  cardAmenityTags,
  type SyndicatedCardRow,
} from '../../../lib/api/syndicatedQuery';

/** Which table the row came from. Decides what the card may say about it. */
export type HomeListingSource = 'listing' | 'syndicated';

/**
 * The price, already composed.
 *
 * SYND-11's rule is that the published figure is the figure and our
 * arithmetic is fine print beneath it, and the enforcement is that neither is
 * assembled at the card: `syndicatedPriceLine` composes both, here, once. A
 * card that could compose its own price line is a card that could compose a
 * different one from the detail page.
 */
export interface HomePriceLine {
  /** "KES 9,000,000" / "From USD 210,000" / "Price on request". */
  label: string;
  /** False for "Price on request" — nobody announced a number. */
  announced: boolean;
  /**
   * SYND-11's marked approximation, for foreign-currency rows only. Null on
   * every marketplace row and on every row the seller priced in KES.
   */
  kesApprox: string | null;
  /**
   * The KES amount the USD buyer-toggle may convert, or null when the toggle
   * must stand down — which is every row not priced in KES. Passed to
   * <UsdApprox> exactly as SYND-11 requires.
   */
  usdToggleKES: number | null;
  usdToggleMaxKES: number | null;
}

export interface HomeListingView {
  source: HomeListingSource;
  /** Row id — also the React key and the save-toggle argument. */
  id: string;
  /**
   * Route to the row. `/property/:slug` for a marketplace listing,
   * `/syndicated/:id` for an indexed one — SYND-06's destinations, unchanged.
   */
  href: string;
  /** Raw storage URL. Callers size it through utils/imageUrl. */
  imageUrl: string | null;
  /** SYND-02: an indexed photo is rendered ONLY with its credit. */
  photoCredit: string | null;
  /** "Apartment" / "Townhouse" / … Rendered upper-case by the card. */
  typeLabel: string;
  /** The raw `property_type` value. Drives which facts the card shows. */
  propertyType: string;
  /**
   * The card heading.
   *
   * TECH-044 reports that PropertyListCard reads a non-existent `property.title`.
   * It exists: `listings.title` is a real column (lib/api/types.ts DbListing) and
   * `dbListingToProperty` maps it. What is true is that it is NULLABLE, which is
   * why both the current card and this mapper fall back to `address`.
   */
  title: string;
  /** "Kilimani, Nairobi", or just the city when there is no neighborhood. */
  areaLabel: string;
  /** The composed price. See HomePriceLine. */
  price: HomePriceLine;
  areaSqm: number | null;
  beds: number | null;
  /** Upper bound of an announced or development bed range. */
  bedsMax: number | null;
  baths: number | null;
  /** Gallery size, for the "1/N" chip. Null when the row carries no array. */
  photoCount: number | null;
  /**
   * Who to credit. An agency/agent for a marketplace row; the company that
   * published the announcement for an indexed one — where it is not optional:
   * "Listed by {source}" is on every indexed card by policy (SYND-01).
   */
  agentLabel: string | null;
  /**
   * Agent verification, badge-only and always optional.
   *
   * FALSE ON EVERY ROW TODAY (HOME-04), and false FOREVER on syndicated rows:
   * /syndication promises in writing that we never mark an indexed listing as
   * verified, and the outreach email quotes that page.
   */
  verified: boolean;
  /** Paid 7-day boost. Buys carousel placement only, never feed ranking. */
  isSpotlight: boolean;
  /**
   * The date the freshness line is derived from — `created_at` for a listing
   * (when the agent listed it) and `indexed_at` for an indexed row (when WE
   * found it). Two different facts, which is why `freshnessTag` below phrases
   * them differently and why they are never mixed into one column here.
   */
  datedAt: string | null;
  /**
   * SYND-18 — which source published an indexed row: `source_slug`, which is
   * NOT NULL and UNIQUE on `syndication_sources`. Set on indexed rows only;
   * marketplace rows omit it, because the per-source cap in "New this week"
   * applies to indexed rows and never to an agent's own listings
   * (marketplaceFirst.ts). `listed_by` is not a substitute: it is a display
   * name, and two sources can share one.
   */
  sourceKey?: string | null;

  // --- Adaptive facts (fix round 1). All optional in the data sense: absent
  // --- means the column is null, and the card renders nothing rather than a
  // --- zero, a dash, or the word "undefined".
  /** KES per month. Apartments only, in the design. */
  serviceCharge: number | null;
  /** 'freehold' / 'leasehold' / … Plot cards show it when set. */
  titleDeedType: string | null;
  /** Plot facts, worth more on land than a gym is. */
  beaconed: boolean;
  surveyApproved: boolean;
  /** Apartment-shaped amenities. */
  gym: boolean;
  pool: boolean;
  backupGenerator: boolean;
  gatedCommunity: boolean;
  /** How many units a development announced. Length of the JSONB array. */
  unitCount: number | null;
  /** SYND-06's hand-authored tags. Indexed rows only. */
  syndicatedAmenities: string[];
}

/** The minimum an SPA `Property` has to expose. Both Property types satisfy it. */
export interface PropertyLike {
  id: string;
  slug?: string | null;
  title?: string | null;
  address?: string | null;
  city?: string | null;
  neighborhood?: string | null;
  price: number;
  currency?: string | null;
  area?: number | null;
  rooms?: number | null;
  roomsMax?: number | null;
  bathrooms?: number | null;
  type?: string | null;
  image?: string | null;
  images?: string[] | null;
  isSpotlight?: boolean;
  createdAt?: string | null;
  serviceCharge?: number | null;
  titleDeedType?: string | null;
  beaconed?: boolean;
  surveyApproved?: boolean;
  gym?: boolean;
  pool?: boolean;
  backupGenerator?: boolean;
  gatedCommunity?: boolean;
  units?: unknown[] | null;
  agent?: {
    name?: string | null;
    agency_name?: string | null;
  } | null;
}

/** The minimum an SSR homepage card row has to expose. */
export interface SsrListingLike {
  id: string;
  slug?: string | null;
  title?: string | null;
  address?: string | null;
  city?: string | null;
  neighborhood?: string | null;
  price: number;
  currency?: string | null;
  area_sqm?: number | null;
  beds?: number | null;
  beds_max?: number | null;
  baths?: number | null;
  property_type?: string | null;
  image_url?: string | null;
  images?: string[] | null;
  agent_name?: string | null;
  agency_name?: string | null;
  is_spotlight?: boolean;
  created_at?: string | null;
  service_charge?: number | null;
  title_deed_type?: string | null;
  beaconed?: boolean | null;
  survey_approved?: boolean | null;
  gym?: boolean | null;
  pool?: boolean | null;
  backup_generator?: boolean | null;
  gated_community?: boolean | null;
  units?: unknown[] | null;
}

/**
 * The heading, with the fallback the current card already uses.
 * Never returns an empty string — a card with no heading is a broken card, and
 * the address is the last thing that is always present on a published listing.
 */
function headingFor(title?: string | null, address?: string | null, city?: string | null): string {
  return (title || '').trim() || (address || '').trim() || (city || '').trim() || 'Property';
}

function localityFor(neighborhood?: string | null, city?: string | null): string {
  const hood = (neighborhood || '').trim();
  const town = (city || '').trim();
  if (hood && town) return `${hood}, ${town}`;
  return hood || town;
}

/**
 * A marketplace price line.
 *
 * CURR-03's `formatListingPrice` composes it — "KES 1,500,000", never "Ksh".
 * Applied HERE rather than at the card so that both card families and both
 * render trees get their price from one function call. HOME-06 existed because
 * the old All-properties cards reached for `formatCurrency` instead and the
 * page printed two spellings of one currency; routing every price through this
 * mapper is what makes that unrepeatable rather than merely fixed.
 *
 * The USD toggle acts on KES amounts only (CURR-01/02) — the same rule that
 * stands it down on SYND-11's foreign-currency rows, applied to marketplace
 * rows by the same field.
 */
function marketplacePrice(amount: number, currency: string): HomePriceLine {
  const code = (currency || 'KES').toUpperCase();
  return {
    label: formatListingPrice(amount, code),
    announced: true,
    kesApprox: null,
    usdToggleKES: code === 'KES' ? amount : null,
    usdToggleMaxKES: null,
  };
}

/** A number a card may print, or null. Guards NaN as well as null/0. */
function positive(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

export function listingViewFromProperty(p: PropertyLike): HomeListingView {
  const currency = p.currency || 'KES';
  return {
    source: 'listing',
    id: p.id,
    href: `/property/${p.slug || p.id}`,
    imageUrl: p.images?.length ? p.images[0] : p.image ?? null,
    photoCredit: null,
    typeLabel: formatPropertyType(p.type || ''),
    propertyType: (p.type || '').toLowerCase(),
    title: headingFor(p.title, p.address, p.city),
    areaLabel: localityFor(p.neighborhood, p.city),
    price: marketplacePrice(p.price, currency),
    areaSqm: positive(p.area),
    beds: positive(p.rooms),
    bedsMax: positive(p.roomsMax),
    baths: positive(p.bathrooms),
    photoCount: p.images?.length ? p.images.length : null,
    agentLabel: p.agent?.agency_name || p.agent?.name || null,
    verified: false,
    isSpotlight: !!p.isSpotlight,
    datedAt: p.createdAt ?? null,
    serviceCharge: positive(p.serviceCharge),
    titleDeedType: (p.titleDeedType || '').trim() || null,
    beaconed: !!p.beaconed,
    surveyApproved: !!p.surveyApproved,
    gym: !!p.gym,
    pool: !!p.pool,
    backupGenerator: !!p.backupGenerator,
    gatedCommunity: !!p.gatedCommunity,
    unitCount: Array.isArray(p.units) && p.units.length > 0 ? p.units.length : null,
    syndicatedAmenities: [],
  };
}

export function listingViewFromSsrRow(row: SsrListingLike): HomeListingView {
  return {
    source: 'listing',
    id: row.id,
    href: `/property/${row.slug || row.id}`,
    imageUrl: row.images?.length ? (row.images[0] as string) : row.image_url ?? null,
    photoCredit: null,
    typeLabel: formatPropertyType(row.property_type || ''),
    propertyType: (row.property_type || '').toLowerCase(),
    title: headingFor(row.title, row.address, row.city),
    areaLabel: localityFor(row.neighborhood, row.city),
    price: marketplacePrice(row.price, row.currency || 'KES'),
    areaSqm: positive(row.area_sqm),
    beds: positive(row.beds),
    bedsMax: positive(row.beds_max),
    baths: positive(row.baths),
    photoCount: row.images?.length ? row.images.length : null,
    agentLabel: row.agency_name || row.agent_name || null,
    verified: false,
    isSpotlight: !!row.is_spotlight,
    datedAt: row.created_at ?? null,
    serviceCharge: positive(row.service_charge),
    titleDeedType: (row.title_deed_type || '').trim() || null,
    beaconed: !!row.beaconed,
    surveyApproved: !!row.survey_approved,
    gym: !!row.gym,
    pool: !!row.pool,
    backupGenerator: !!row.backup_generator,
    gatedCommunity: !!row.gated_community,
    unitCount: Array.isArray(row.units) && row.units.length > 0 ? row.units.length : null,
    syndicatedAmenities: [],
  };
}

/**
 * An indexed row, mapped for the same card the marketplace rows render in —
 * SYND-06's mirror, now that "whichever card the list is showing" is this one.
 *
 * WHAT IS DELIBERATELY EMPTY, AND WHY
 *   baths, areaSqm    the columns do not exist on `syndicated_public`. An
 *                     announcement states neither, so the card shows a shorter
 *                     complete line rather than "undefined m²" or an icon slot
 *                     with nothing beside it.
 *   verified          /syndication promises we never mark an indexed listing
 *                     as verified. Not "not yet".
 *   isSpotlight       Spotlight is a placement an Afriqahome agent buys; an
 *                     announcement has no agent to buy it, and
 *                     `syndicated_listings` has no `spotlight_until` column.
 *   photoCount        exactly one photo, always. A "1/1" chip is noise.
 *
 * `agentLabel` is `listed_by` and is never null: naming the company selling it
 * is policy, not decoration.
 *
 * SYND-18: the input is `SyndicatedCardRow`, exactly the columns this mapper
 * reads, so the server render can select those and nothing else. A full
 * `SyndicatedListing` satisfies it.
 */
export function listingViewFromSyndicated(row: SyndicatedCardRow): HomeListingView {
  const priceLine = syndicatedPriceLine(row);

  return {
    source: 'syndicated',
    id: row.id,
    href: `/syndicated/${row.id}`,
    imageUrl: row.photo_url ?? null,
    photoCredit: row.photo_credit ?? null,
    typeLabel: formatPropertyType(row.property_type || ''),
    propertyType: (row.property_type || '').toLowerCase(),
    title: row.title,
    areaLabel: formatSyndicatedLocation(row),
    price: {
      label: priceLine.label,
      announced: priceLine.announced,
      kesApprox: priceLine.kesApprox,
      usdToggleKES: priceLine.toggleKes ? priceLine.toggleKes.from : null,
      usdToggleMaxKES: priceLine.toggleKes ? priceLine.toggleKes.to ?? null : null,
    },
    areaSqm: null,
    beds: positive(row.beds_min),
    bedsMax: positive(row.beds_max),
    baths: null,
    photoCount: null,
    agentLabel: row.listed_by,
    verified: false,
    isSpotlight: false,
    datedAt: row.indexed_at ?? null,
    sourceKey: row.source_slug ?? null,
    serviceCharge: null,
    titleDeedType: null,
    beaconed: false,
    surveyApproved: false,
    gym: false,
    pool: false,
    backupGenerator: false,
    gatedCommunity: false,
    unitCount: null,
    syndicatedAmenities: cardAmenityTags(row.amenities),
  };
}

/**
 * SYND-15 — what saving THIS card writes to.
 *
 * `source` is the discriminant the card has carried since HOME-01 fix round 1,
 * and it is the only thing that can answer the question: the two id spaces are
 * both uuids from different tables, so `id` alone cannot. Every heart in the
 * redesign family goes through here rather than composing `{ kind, id }` at the
 * call site — a call site that composes its own target is a call site that can
 * compose the wrong one.
 */
export function savedTargetFromView(view: Pick<HomeListingView, 'source' | 'id'>): SavedTarget {
  return { kind: view.source === 'syndicated' ? 'syndicated' : 'listing', id: view.id };
}

/** The key a `savedIds` set holds for this card. Never the bare uuid. */
export function savedKeyFromView(view: Pick<HomeListingView, 'source' | 'id'>): string {
  return savedTargetKey(savedTargetFromView(view));
}

/** Whole days between `iso` and now. Negative clock skew clamps to 0. */
export function daysSince(iso: string | null, now: number = Date.now()): number | null {
  if (!iso) return null;
  const stamp = Date.parse(iso);
  if (Number.isNaN(stamp)) return null;
  return Math.max(0, Math.floor((now - stamp) / 86_400_000));
}

/** How many days back "New this week" reaches. */
export const NEW_THIS_WEEK_DAYS = 7;

/**
 * The freshness line — and it says a DIFFERENT THING for each source, because
 * the two dates are different facts.
 *
 *   listing     `created_at` is when the agent listed it. "Listed today".
 *   syndicated  `indexed_at` is when WE found it. "Added today".
 *
 * A row we indexed this morning can be an announcement from last quarter, so
 * "Listed today" on it would be a fabricated fact about someone else's
 * listing — the reason indexed rows were once held out of "New this week"
 * altogether. They can carry a freshness line as long as it says what actually
 * happened, which is that we added it; SYND-18 (PO decision D2, 2026-10-13)
 * puts them in "New this week" on exactly that footing, after the marketplace
 * cards.
 *
 * Returns null when there is no date: the decision for a row with no timestamp
 * is to drop the line, not to guess a day.
 */
export function freshnessTag(
  datedAt: string | null,
  source: HomeListingSource = 'listing',
  now: number = Date.now()
): string | null {
  const days = daysSince(datedAt, now);
  if (days === null) return null;
  const verb = source === 'syndicated' ? 'Added' : 'Listed';
  if (days === 0) return `${verb} today`;
  if (days === 1) return `${verb} 1 day ago`;
  return `${verb} ${days} days ago`;
}
